import { DateTime } from 'luxon';

import {
    IOfferEventDetailFragment,
    IOfferEventSubscriptionStatus,
    IOfferEventType,
    ILabels,
    IExtraCategoryOption,
    IExtraCategoryValueFragment,
    IAttendanceStatusChoices,
    IOfferEventEnrollmentManageableFragment,
    IOfferEventEnrollmentManagementListItemFragment,
} from 'graphql/types';
import { translate } from 'utils/i18n';
import { getRoundedDateTime, valueToDateTime } from 'common/utils/formatDate';
import { TColorKeys } from 'theme/palette';
import { parseJSON } from 'common/utils/json';
import { IEnrollmentAttendanceDialogValues } from 'offer/components/EnrollmentAttendanceDialog';

export function getOfferEventSubscriptionStatusData(
    subscriptionStatus?: IOfferEventSubscriptionStatus
): { color: TColorKeys | 'default'; label: string } | undefined {
    if (!subscriptionStatus) return { color: 'primary', label: '' };

    const transPrefix = 'enrollmentsTable';

    switch (subscriptionStatus) {
        case IOfferEventSubscriptionStatus.Requested:
            return {
                color: 'warning',
                label: translate(`${transPrefix}.labelRequested`),
            };
        case IOfferEventSubscriptionStatus.Denied:
            return {
                color: 'error',
                label: translate(`${transPrefix}.labelRejected`),
            };
        case IOfferEventSubscriptionStatus.Completed:
            return {
                color: 'success',
                label: translate(`${transPrefix}.labelCompleted`),
            };
        case IOfferEventSubscriptionStatus.Absent:
            return {
                color: 'error',
                label: translate(`${transPrefix}.labelAbsent`),
            };
        default:
            return undefined;
    }
}

export function getOfferEventTypeTranslation(type: IOfferEventType) {
    return translate(type.toLowerCase()) || '';
}

export function getOfferEventFormData(
    offerEventType: IOfferEventType,
    offerEvent?: IOfferEventDetailFragment,
    hasServiceProvider?: boolean
) {
    const commonValues = {
        initialValues: {
            dates: offerEvent?.dates.map((date) => {
                // If no location is set we use the locationString as location
                const location = date.location || {
                    title: date.locationString,
                };

                return {
                    ...date,
                    startDate: DateTime.fromISO(date.startDate),
                    endDate: DateTime.fromISO(date.endDate),
                    location,
                    webinarLink:
                        date.webinarLink.replace(/(^\w+:|^)\/\//, '') || '',
                    trainers: date.trainers?.map((trainer) => trainer.id),
                };
            }) || [
                {
                    startDate: getRoundedDateTime(),
                    endDate: getRoundedDateTime().plus({ hours: 1 }),
                    isServiceProviderWebinar: hasServiceProvider,
                    location: null,
                    webinarLink: '',
                },
            ],
            minSubscriptions: offerEvent?.minSubscriptions ?? 0,
            maxSubscriptions: offerEvent?.maxSubscriptions ?? 0,
            openingDate: valueToDateTime(offerEvent?.openingDate) || null,
            closingDate: valueToDateTime(offerEvent?.closingDate) || null,
            unenrollClosingDate:
                valueToDateTime(offerEvent?.unenrollClosingDate) || null,
            showParticipantList: !!offerEvent?.showParticipantList || false,
            publicParticipantList: !!offerEvent?.publicParticipantList || false,
            offerEventTitle: offerEvent?.title,
            type: offerEventType,
            scoreThreshold:
                offerEventType === IOfferEventType.Meeting ||
                offerEventType === IOfferEventType.Webinar
                    ? undefined
                    : offerEvent?.scoreThreshold || 0,
        },
    };

    if (
        [IOfferEventType.Meeting, IOfferEventType.Webinar].includes(
            offerEventType
        )
    ) {
        return {
            initialValues: {
                ...commonValues.initialValues,
            },
        };
    }

    if (offerEventType === IOfferEventType.Training) {
        return {
            initialValues: {
                showParticipantList:
                    commonValues.initialValues.showParticipantList,
                type: commonValues.initialValues.type,
                scoreThreshold: commonValues.initialValues.scoreThreshold,
            },
        };
    }

    return { initialValues: {}, validation: {} };
}

/*
 * Checks if a user can subscribe to an offer event
 */
export function getIsOfferEventSubscribable(
    offerEvent: IOfferEventDetailFragment,
    canUpdate: boolean,
    canManageSubscriptions?: boolean
) {
    const { currentUserEnrollment } = offerEvent;

    // If the user can update, can manage subscriptions or the user already has a subscription that is not denied
    // the user can't subscribe
    if (
        canUpdate ||
        canManageSubscriptions ||
        (currentUserEnrollment &&
            currentUserEnrollment.status !==
                IOfferEventSubscriptionStatus.Denied)
    ) {
        return false;
    }

    const { type, closingDate } = offerEvent;
    const isTraining = type === IOfferEventType.Training;

    // Trainings can always be subscribed to
    // If closing date is in the past it is not subscribable any longer
    if (!isTraining && closingDate) {
        const closingDatePast = DateTime.fromISO(closingDate) < DateTime.now();

        if (closingDatePast) return false;
    }

    return true;
}

/*
 * Checks whether enrollment has not opened yet because the opening date lies in
 * the future. While enrollment is not open the user cannot subscribe yet — the
 * subscribe button is replaced by a "you can enroll from" message.
 */
export function getIsEnrollmentNotYetOpen(
    offerEvent: IOfferEventDetailFragment
) {
    const { openingDate, openingDateReached } = offerEvent;

    return !!openingDate && !openingDateReached;
}

/*
 * Checks if the unsubscribe section should be shown for a user.
 *
 * Note: the unenroll deadline (closingDate / unenrollClosingDate) does not hide
 * the section here — instead the unenroll button is shown disabled with a
 * tooltip once the deadline has passed (see `getIsUnenrollClosed`).
 */
export function getIsOfferEventUnsubscribable(
    canUpdate: boolean,
    canManageSubscriptions?: boolean
) {
    // If the user can update or can manage subscriptions the user can't unsubscribe
    return !canUpdate && !canManageSubscriptions;
}

/*
 * Checks whether unenrolling is no longer possible because the unenroll
 * deadline has passed. The dedicated `unenrollClosingDate` governs the deadline
 * when set; otherwise it falls back to the `closingDate`.
 */
export function getIsUnenrollClosed(offerEvent: IOfferEventDetailFragment) {
    const {
        type,
        unenrollClosingDate,
        unenrollClosingDateExpired,
        closingDateExpired,
    } = offerEvent;

    const isWebinarOrMeeting =
        type === IOfferEventType.Webinar || type === IOfferEventType.Meeting;

    // Only webinars and meetings have an enrollment/unenroll deadline.
    if (!isWebinarOrMeeting) return false;

    return unenrollClosingDate
        ? unenrollClosingDateExpired
        : closingDateExpired;
}

/*
 * Returns the label translations for the offer event labels
 */
export function getLabelTranslation(label: ILabels) {
    if (label === ILabels.Trainer) return translate('trainer');

    return '';
}

// Get the date from local storage if present
export function getOfferEventOverviewDefaultFilterDate() {
    const nowDate = DateTime.now();
    const defaultFilterDate = nowDate.startOf('day').minus({ months: 1 });

    const storageDateFilter = localStorage.getItem(
        'offerEventOverviewDateFilter'
    );
    const storageDateFilterParsed = storageDateFilter
        ? parseJSON(storageDateFilter)
        : undefined;

    if (!storageDateFilterParsed) return defaultFilterDate;

    const storageDate = DateTime.fromISO(storageDateFilterParsed.date);
    const storageSetDate = DateTime.fromISO(storageDateFilterParsed.setDate);

    // If there is no valid storage date or storage set date
    // If the storage setDate is more than a day old its considered expired
    // If storage date is a date in the future its invalid
    if (
        !storageDate.isValid ||
        !storageSetDate.isValid ||
        storageSetDate.diffNow('days').days < -1 ||
        storageDate.startOf('day') > nowDate
    ) {
        localStorage.removeItem('offerEventOverviewDateFilter');

        return defaultFilterDate;
    }

    return storageDate;
}

export function filterExtraCategoryValuesForOfferEvent(
    extraCategoryValues: IExtraCategoryValueFragment[]
) {
    return extraCategoryValues.filter((group) => {
        const { categoryType, showLabel } = group.category;

        return (
            categoryType !== IExtraCategoryOption.Boolean &&
            categoryType !== IExtraCategoryOption.Datetime &&
            showLabel
        );
    });
}

/*
 * Returns the attendance values for a specific offer event date and a list of users (enrollments)
 */
export function getEnrollmentAttendanceValues(
    currentDateAttendanceValue: IEnrollmentAttendanceDialogValues[keyof IEnrollmentAttendanceDialogValues],
    status: IAttendanceStatusChoices,
    enrollments: (
        | IOfferEventEnrollmentManageableFragment
        | IOfferEventEnrollmentManagementListItemFragment
    )[]
) {
    const selectedUserIds = enrollments.map((enrollment) => enrollment.user.id);

    const isPresent = status === IAttendanceStatusChoices.Present;

    const currentStatusUserListKey = isPresent
        ? 'presentUserIds'
        : 'absentUserIds';

    let currentStatusUserList = [
        ...(currentDateAttendanceValue[currentStatusUserListKey] || []),
    ];
    const noStatusUserList = [];

    if (enrollments.length > 1) {
        // If all enrollments are already in the list, clear the list and add them to the
        // "No Status" list
        if (
            enrollments.every(
                (enrollment) =>
                    !!currentStatusUserList?.includes(enrollment.user.id)
            )
        ) {
            currentStatusUserList = [];

            noStatusUserList.push(...selectedUserIds);
        } else {
            // Only add user ids that are not already in the list
            currentStatusUserList = [
                ...currentStatusUserList,
                ...selectedUserIds.filter(
                    (userId) => !currentStatusUserList?.includes(userId)
                ),
            ];
        }
    } else {
        const enrollment = enrollments[0];

        // If already in the list remove the user id from the list and add them to the
        // "No Status" list
        if (currentStatusUserList.includes(enrollment.user.id)) {
            currentStatusUserList = currentStatusUserList.filter(
                (userId) => userId !== enrollment.user.id
            );

            noStatusUserList.push(enrollment.user.id);
        } else {
            currentStatusUserList = [
                ...currentStatusUserList,
                ...selectedUserIds,
            ];
        }
    }

    const otherStatusUserListKey = isPresent
        ? 'absentUserIds'
        : 'presentUserIds';

    // Remove the user ids from the list of the other status
    const otherStatusUserList =
        currentDateAttendanceValue[otherStatusUserListKey]?.filter(
            (userId) => !currentStatusUserList.includes(userId)
        ) || [];

    return {
        ...currentDateAttendanceValue,
        [currentStatusUserListKey]: currentStatusUserList,
        [otherStatusUserListKey]: otherStatusUserList,
        noStatusUserIds: noStatusUserList,
    };
}

export function getOfferEventStatusTranslation(
    offerEvent: IOfferEventDetailFragment,
    canUpdate: boolean,
    canManageSubscriptions: boolean
): { color?: TColorKeys; label: string } | undefined {
    const { closingDateExpired, currentUserEnrollment, publish, cancelled } =
        offerEvent;

    const showClosingDateExpiredChip =
        closingDateExpired &&
        (!currentUserEnrollment || canUpdate || canManageSubscriptions);

    if (cancelled) return { color: 'error', label: translate('cancelled') };

    if (!publish) return { color: 'warning', label: translate('concept') };

    if (showClosingDateExpiredChip)
        return {
            label: translate('subscriptionClosed'),
        };

    return undefined;
}
